import React, { useState, useEffect, useRef } from 'react';
import { X, Search } from 'lucide-react';
import ContentCard from './ContentCard';

const SearchModal = ({ isOpen, onClose, allContent, contentType }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }

    // Add keyboard listener for ESC key
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setSearchResults([]);
      return;
    }

    // Filter content in real-time
    const filtered = allContent.filter(item =>
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setSearchResults(filtered);
  }, [searchQuery, allContent]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>

      {/* Modal Content */}
      <div 
        className="relative w-full max-w-4xl bg-white/5 backdrop-blur-3xl border border-white/20 rounded-2xl shadow-2xl shadow-electric/30 overflow-hidden"
        style={{ backdropFilter: 'blur(40px) saturate(180%)' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="search-modal"
      >
        {/* Header with Search Input */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center space-x-4">
            <Search className="h-6 w-6 text-electric flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              placeholder={`Search ${contentType}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent text-2xl text-foreground placeholder-foreground/40 focus:outline-none"
              data-testid="search-modal-input"
            />
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              data-testid="close-search-button"
            >
              <X className="h-6 w-6 text-foreground" />
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto p-6">
          {searchQuery.trim() === '' ? (
            <div className="text-center py-12">
              <Search className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-muted-foreground text-lg">Start typing to search...</p>
            </div>
          ) : searchResults.length > 0 ? (
            <div>
              <p className="text-sm text-muted-foreground mb-4">
                Found {searchResults.length} {searchResults.length === 1 ? 'result' : 'results'}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {searchResults.map((item) => (
                  <ContentCard key={item.id} content={item} />
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground text-lg">No results found for "{searchQuery}"</p>
              <p className="text-muted-foreground text-sm mt-2">Try a different search term</p>
            </div>
          )}
        </div>

        {/* Keyboard Hint */}
        <div className="px-6 py-3 border-t border-white/10 bg-white/5">
          <p className="text-xs text-muted-foreground text-center">
            Press <kbd className="px-2 py-1 rounded bg-white/10 border border-white/20 text-foreground">ESC</kbd> to close
          </p>
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
